import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANG_OPTIONS } from "@/lib/i18n.jsx";

export default function AuthPage() {
  const { t, lang, setLang } = useI18n();
  const { login, signup } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ full_name: "", dealership_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [tachAngle, setTachAngle] = useState(-110);

  // Animated speedometer needle on mount
  useEffect(() => {
    const target = -110 + Math.random() * 30;
    const id = setTimeout(() => setTachAngle(target), 250);
    return () => clearTimeout(id);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Rev the needle on submit
    setTachAngle(140);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await signup(form);
      toast.success(t("saved"));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail === "Invalid credentials" ? t("invalid_credentials") :
                  detail === "Email already in use" ? t("email_in_use") :
                  detail || t("error_generic"));
      setTimeout(() => setTachAngle(-90), 400);
    } finally { setLoading(false); }
  };

  return (
    <div
      data-testid="auth-page"
      className="min-h-screen text-white flex relative overflow-hidden xpro-auth-bg"
    >
      {/* Carbon fiber texture overlay */}
      <div className="absolute inset-0 pointer-events-none xpro-carbon" />
      {/* Diagonal red speed-lines */}
      <div className="absolute inset-0 pointer-events-none xpro-speedlines" />
      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none xpro-vignette" />

      {/* Top crimson racing stripe with checker accent */}
      <div className="absolute top-0 inset-x-0 h-[3px] z-30 xpro-top-stripe" />

      {/* Lang switcher (top-right, glass pill) */}
      <div className="absolute top-5 right-5 flex gap-1.5 z-30" data-testid="lang-switcher">
        {LANG_OPTIONS.map((l) => (
          <button
            key={l.code}
            data-testid={`lang-${l.code}`}
            onClick={() => setLang(l.code)}
            className={`px-3 h-8 text-[11px] font-display font-bold uppercase tracking-[0.18em] rounded-full backdrop-blur transition-all border ${
              lang === l.code
                ? "bg-[#E11D2B] text-white border-[#E11D2B] shadow-[0_0_16px_rgba(225,29,43,0.6)]"
                : "bg-black/40 text-white/60 border-white/10 hover:border-white/40 hover:text-white"
            }`}
          >
            {l.flag} {l.label}
          </button>
        ))}
      </div>

      {/* ===================== LEFT: HERO BRAND ===================== */}
      <section className="hidden lg:flex flex-1 relative items-center justify-center px-12 py-12 z-10">
        {/* Speedometer halo behind logo */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="xpro-speedo-halo" />
        </div>

        <div className="relative max-w-lg w-full">
          {/* Eyebrow with live dot */}
          <div className="flex items-center gap-2 mb-6">
            <span className="xpro-live-dot" />
            <span className="text-[10px] uppercase tracking-[0.5em] text-[#E11D2B] font-display font-bold">
              Management Suite · v2
            </span>
          </div>

          {/* Logo with red glow */}
          <div className="relative mb-8 xpro-logo-wrap">
            <div className="xpro-logo-glow" />
            <img
              src="/xpro-logo.png"
              alt="X-PRO MOTORS"
              className="relative w-full max-w-[420px] object-contain xpro-logo-img"
            />
          </div>

          {/* Big slogan */}
          <h1 className="font-display font-black text-[44px] leading-[0.95] tracking-tight mb-4">
            <span className="block text-white">DRIVE YOUR</span>
            <span className="block xpro-text-foil">DEALERSHIP</span>
            <span className="block text-white/40">FORWARD.</span>
          </h1>

          <p className="text-sm text-white/55 leading-relaxed max-w-md mb-10">
            {t("tagline")}
          </p>

          {/* Stat row */}
          <div className="grid grid-cols-3 gap-px bg-white/5 border border-white/10 rounded-sm overflow-hidden">
            {[
              { kpi: "100%", label: "INVENTORY" },
              { kpi: "24/7", label: "PIPELINE" },
              { kpi: "PRO", label: "ANALYTICS" },
            ].map((s, i) => (
              <div key={i} className="bg-black/60 backdrop-blur px-4 py-4 text-center">
                <div className="font-display font-black text-xl text-white">{s.kpi}</div>
                <div className="text-[9px] tracking-[0.3em] text-white/40 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== RIGHT: AUTH PANEL ===================== */}
      <section className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <div className="w-full max-w-md relative">
          {/* Mobile-only mini logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <div className="relative">
              <div className="xpro-logo-glow xpro-logo-glow-sm" />
              <img
                src="/xpro-logo.png"
                alt="X-PRO MOTORS"
                className="relative w-40 object-contain"
              />
            </div>
          </div>

          {/* Tachometer ring around card */}
          <div className="relative">
            <svg
              className="absolute -top-6 -left-6 w-16 h-16 xpro-tach-svg"
              viewBox="0 0 64 64"
              fill="none"
            >
              <circle cx="32" cy="32" r="26" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
              <path
                d="M 32 32 L 32 8"
                stroke="#E11D2B"
                strokeWidth="2.5"
                strokeLinecap="round"
                style={{
                  transformOrigin: "32px 32px",
                  transform: `rotate(${tachAngle}deg)`,
                  transition: "transform 900ms cubic-bezier(.34,1.56,.64,1)",
                  filter: "drop-shadow(0 0 4px #E11D2B)",
                }}
              />
              <circle cx="32" cy="32" r="3" fill="#E11D2B" />
            </svg>

            {/* Corner brackets */}
            <span className="xpro-corner xpro-corner-tl" />
            <span className="xpro-corner xpro-corner-tr" />
            <span className="xpro-corner xpro-corner-bl" />
            <span className="xpro-corner xpro-corner-br" />

            {/* Glass form card */}
            <div className="relative xpro-card p-8">
              {/* Tabs */}
              <div className="flex mb-7 border-b border-white/10">
                <button
                  data-testid="tab-login"
                  onClick={() => setMode("login")}
                  className={`flex-1 pb-3 text-[11px] tracking-[0.35em] font-display font-bold uppercase transition-colors relative ${
                    mode === "login" ? "text-white" : "text-white/35 hover:text-white/70"
                  }`}
                >
                  {t("sign_in")}
                  {mode === "login" && <span className="xpro-tab-underline" />}
                </button>
                <button
                  data-testid="tab-signup"
                  onClick={() => setMode("signup")}
                  className={`flex-1 pb-3 text-[11px] tracking-[0.35em] font-display font-bold uppercase transition-colors relative ${
                    mode === "signup" ? "text-white" : "text-white/35 hover:text-white/70"
                  }`}
                >
                  {t("create_account")}
                  {mode === "signup" && <span className="xpro-tab-underline" />}
                </button>
              </div>

              <div className="mb-6">
                <p className="text-[10px] text-[#E11D2B] tracking-[0.45em] font-bold uppercase mb-2">
                  {mode === "login" ? "Access Terminal" : "Pit Crew Onboarding"}
                </p>
                <h2 className="font-display font-black text-2xl uppercase tracking-tight text-white">
                  {mode === "login" ? t("welcome_back") : t("create_account_title")}
                </h2>
              </div>

              <form onSubmit={submit} className="space-y-5">
                {mode === "signup" && (
                  <>
                    <Field label={t("full_name")} testid="auth-name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required />
                    <Field label={t("dealership_name")} testid="auth-dealership" value={form.dealership_name} onChange={(v) => setForm({ ...form, dealership_name: v })} required />
                  </>
                )}
                <Field label={t("email")} type="email" testid="auth-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                <Field label={t("password")} type="password" testid="auth-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required />

                <button
                  type="submit"
                  data-testid="auth-submit"
                  disabled={loading}
                  className="xpro-cta-btn w-full mt-2 group"
                >
                  <span className="xpro-cta-arrow">
                    {loading ? (
                      <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"/>
                      </svg>
                    )}
                  </span>
                  <span className="xpro-cta-label">
                    {loading ? "ENGAGING..." : mode === "login" ? "IGNITION · ENTER" : "START ENGINE"}
                  </span>
                </button>
              </form>

              <div className="mt-7 pt-5 border-t border-white/5 flex items-center justify-between text-[11px]">
                <span className="text-white/35 tracking-wider uppercase">
                  {mode === "login" ? t("no_account") : t("have_account")}
                </span>
                <button
                  data-testid="auth-toggle"
                  onClick={() => setMode(mode === "login" ? "signup" : "login")}
                  className="text-[#E11D2B] hover:text-white font-display font-bold tracking-[0.2em] uppercase transition-colors"
                >
                  {mode === "login" ? "SIGN UP →" : "SIGN IN →"}
                </button>
              </div>
            </div>
          </div>

          {/* VIN-style footer line */}
          <div className="mt-8 flex items-center justify-between px-1">
            <p className="text-[9px] tracking-[0.4em] text-white/30 uppercase font-mono">
              VIN · XPRO-2026-MGR-V2
            </p>
            <p className="text-[9px] tracking-[0.4em] text-white/30 uppercase font-mono">
              SECURE · TLS
            </p>
          </div>
        </div>
      </section>

      {/* Bottom thin band */}
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#E11D2B]/40 to-transparent z-20" />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  const [focus, setFocus] = useState(false);
  return (
    <div className="xpro-field-wrap">
      <label className="block text-[9px] font-display font-bold tracking-[0.32em] uppercase mb-2 text-white/45">
        {label}
      </label>
      <div className={`xpro-field ${focus ? "xpro-field-focus" : ""}`}>
        <input
          data-testid={testid}
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          className="w-full bg-transparent border-0 focus:outline-none text-white text-sm font-body px-4 h-11 placeholder-white/20"
        />
        <span className="xpro-field-underline" />
      </div>
    </div>
  );
}
